/*
 * A link opened while signed out (a share email, a bell item on another device) has to
 * survive the login screen. The guard and the 401 handlers save where the user was going, and the
 * password login and the SSO callback consume it once. sessionStorage is per origin and per tab,
 * which is exactly the scope of the sign-in that follows.
 */
const RETURN_PATH_KEY = "post-login-return-path";
const PLACEHOLDER_ORIGIN = "http://return.invalid";

// Only the Files hub and its share links are deep-linked today.
const ALLOWED_PREFIXES = ["/files"];

const hasControlCharacter = (value: string) =>
  [...value].some((char) => char.charCodeAt(0) <= 0x1f || char.charCodeAt(0) === 0x7f);

export function sanitizeReturnPath(value: unknown): string | null {
  if (typeof value !== "string" || !value.startsWith("/")) return null;
  // "//host" and "/\host" are protocol-relative to a browser; control characters can hide either.
  if (value.startsWith("//") || value.includes("\\") || hasControlCharacter(value)) return null;
  let url: URL;
  try {
    url = new URL(value, PLACEHOLDER_ORIGIN);
  } catch {
    return null;
  }
  if (url.origin !== PLACEHOLDER_ORIGIN) return null;
  const allowed = ALLOWED_PREFIXES.some(
    (prefix) => url.pathname === prefix || url.pathname.startsWith(`${prefix}/`),
  );
  return allowed ? `${url.pathname}${url.search}` : null;
}

export function saveReturnPath(path: string): void {
  const safe = sanitizeReturnPath(path);
  if (!safe) return;
  try {
    sessionStorage.setItem(RETURN_PATH_KEY, safe);
  } catch {
    // Storage can be blocked; the user then lands on the home page after login.
  }
}

export function saveCurrentLocationForReturn(): void {
  saveReturnPath(`${window.location.pathname}${window.location.search}`);
}

/** Reads and clears the saved path, re-validating it so a tampered value cannot redirect. */
export function consumeReturnPath(): string | null {
  try {
    const stored = sessionStorage.getItem(RETURN_PATH_KEY);
    sessionStorage.removeItem(RETURN_PATH_KEY);
    return sanitizeReturnPath(stored);
  } catch {
    return null;
  }
}

export function toNavigateTarget(path: string): { to: string; search: Record<string, string> } {
  const url = new URL(path, PLACEHOLDER_ORIGIN);
  return { to: url.pathname, search: Object.fromEntries(url.searchParams) };
}
